import Text from '@sureapp/wl-toggle-common/dist/components/Text';
import produce from 'immer';

import { TConfig, TConfigInput, TConfigParams } from '@@/auto/interfaces';

import classes from '../../../components/CoverageSections/CoverageSectionDropdown/CoverageSectionDropdown.module.css';
import { AUTO_DISCOUNTS } from '../../autoDiscounts';
import { featureFlag } from '../../featureFlags';

function ToggleVAConfig({
  isFeatureEnabled,
}: TConfigParams): DeepPartial<TConfigInput> {
  return isFeatureEnabled(featureFlag.IS_VA_ENABLED)
    ? {
        autoDiscounts: [
          AUTO_DISCOUNTS.affinity,
          AUTO_DISCOUNTS.matureDriver55,
          AUTO_DISCOUNTS.safeDriving,
          AUTO_DISCOUNTS.multiCar,
          AUTO_DISCOUNTS.multiPolicy,
          AUTO_DISCOUNTS.homeowner,
          AUTO_DISCOUNTS.goodStudent,
          AUTO_DISCOUNTS.distantStudent,
          AUTO_DISCOUNTS.teenDriver,
        ],
        matureDriver: () => ({
          getSubjectText: null,
          label:
            'Has this person completed a mature driver motor vehicle accident prevention course in the past 3 years?',
          explanation:
            'Approved defensive driving courses designed for mature drivers help improve driving skills and reduce driving risks by anticipating situations and making safe well-informed decisions. Courses required because of a driving offense or traffic violation do not apply.',
        }),
        sanitizeCoverageRegexRecord: () => ({
          uninsuredMotoristPropertyDamage: /- Alternative|- Standard/g,
          uninsuredMotoristBodilyInjury: /- Alternative|- Standard/g,
        }),
        auto: (auto: TConfig['auto']) =>
          produce(auto, (draft) => {
            if (draft.addOns.options.temporary_ride) {
              draft.addOns.options.temporary_ride.copy.label =
                'Transportation Expenses Coverage';
              draft.addOns.options.temporary_ride.copy.modalContent = `Transportation Expenses Coverage is optional and provides a flat amount per day up to the policy limit for alternative transportation such as ridesharing, car rental, taxicab, or public transit while your vehicle is being repaired or replaced following a covered loss.`;
              draft.addOns.options.temporary_ride.copy.textAlert =
                'Turning on this coverage will cover up to $30 per day of transportation expenses.';
              if (draft.addOns.options.temporary_ride.options) {
                draft.addOns.options.temporary_ride.options.optionsCopy = undefined;
                draft.addOns.options.temporary_ride.options.shouldShowOptions = false;
              }
            }
            if (draft.addOns.options.gap) {
              draft.addOns.options.gap.copy.label = 'Auto Loan/Lease Coverage';
            }
            if (draft.addOns.options.towing_road_services) {
              draft.addOns.options.towing_road_services.copy.label =
                'Towing and Labor Costs Coverage';
              draft.addOns.options.towing_road_services.copy.modalContent =
                'Towing & Labor Costs Coverage is an optional coverage and pays for the cost of towing and other roadside services like a locksmith, flat tire changing, and battery jumpstart in the event your vehicle is disabled (subject to the Service Call conditions).';
            }
            if (draft.addOns.options.oem_parts) {
              draft.addOns.options.oem_parts.copy.label =
                'Original Equipment Manufacturer Parts Coverage';
            }
            if (draft.addOns.options.side_hustle) {
              draft.addOns.options.side_hustle.copy.modalContent = (
                <>
                  Side Hustle Auto is an optional coverage and is applicable if
                  you have part-time employment, earning up to $25,000 annually
                  doing rideshare or on-demand food delivery.
                  <br />
                  <br />
                  If you&apos;re a rideshare driver, it extends the coverage
                  shown on your Declarations Page from the time when you&apos;re
                  available to receive requests to the time you&apos;re engaged
                  in a prearranged ride. Once the prearranged ride has begun, it
                  extends the coverage shown on your Declarations Page except
                  Liability.
                  <br />
                  <br />
                  If your side hustle is on-demand food delivery, it extends the
                  coverage shown on your Declarations Page from the time
                  you&apos;re available to receive a food delivery request to
                  the time you&apos;re engaged in a prearranged delivery. Once
                  the prearranged delivery has begun, it extends the coverage
                  shown on your Declarations Page with two exceptions -
                  Liability coverage up to the minimum limits mandated by your
                  state and Other Than Collision & Collision coverage up to
                  $1,000 or the deductible you selected, whichever is higher.
                  <br />
                  <br />
                  In the event of a covered loss to your vehicle, Side Hustle
                  Auto also covers property damage to your business personal
                  property up to $1,000 as well as loss of business income ($500
                  per week for a maximum of 4 weeks) if you have purchased Other
                  Than Collision & Collision coverage. One claim allowed per
                  policy period.
                </>
              );
            }
          }),
        coverage: (coverage: TConfig['coverage']) =>
          produce(coverage, (draft) => {
            draft.compColl.title = 'Collision & Other than Collision';
            draft.compColl.modalContent = (
              <>
                <strong>Collision</strong> can pay up to the limits chosen for
                accidental damage to your car caused by collision with another
                vehicle or fixed object. Deductibles chosen are per vehicle.
                <br />
                <br />
                <strong>Other Than Collision</strong> can pay up to the limits
                chosen for accidental damage to your car from things other than
                a collision - like floods, hail, fire, theft, and windshield or
                animal damage.
              </>
            );

            draft.uninsuredMotoristBodilyInjury.title =
              'Uninsured/Underinsured Motorist Bodily Injury';
            draft.uninsuredMotoristBodilyInjury.description = `That pain in your neck is not just from the accident - it's also from finding out that the person who hit you has no insurance. This coverage can help.`;
            draft.uninsuredMotoristBodilyInjury.modalContent = (
              <>
                This can help you cover injuries that you or someone on your
                policy gets if you&apos;re hit by a driver who isn&apos;t
                insured, isn&apos;t identified (like a hit-and-run), or
                doesn&apos;t have enough coverage.
                <br />
                <br />
                If you pick{' '}
                <Text
                  variant="inherit"
                  component="strong"
                  className={classes.text_strong_purple}
                >
                  Alternative
                </Text>
                , we&apos;ll pay you up to the difference between the at-fault
                driver&apos;s Liability coverage and your coverage limit.
                <br />
                <br />
                If you pick{' '}
                <Text
                  variant="inherit"
                  component="strong"
                  className={classes.text_strong_purple}
                >
                  Standard
                </Text>
                , we&apos;ll pay for your injuries up to your coverage limit
                without reduction for the amount able to be recovered from the
                at-fault driver&apos;s Liability coverage.
              </>
            );

            draft.uninsuredMotoristPropertyDamage = {
              title: 'Uninsured/Underinsured Motorist Property Damage',
              description: `Your bumper sticker might say 'don't hit me if you're uninsured' but it isn't actually protecting you against that. Don't worry, this coverage can help.`,
              modalContent: (
                <>
                  This can help you cover physical damage to your car if
                  you&apos;re hit by a driver who isn&apos;t insured or
                  doesn&apos;t have enough coverage to cover your vehicle&apos;s
                  damage.
                  <br />
                  <br />
                  If you pick{' '}
                  <Text
                    variant="inherit"
                    component="strong"
                    className={classes.text_strong_purple}
                  >
                    Alternative
                  </Text>
                  , we&apos;ll pay you up to the difference between the at-fault
                  driver&apos;s Liability coverage and your coverage limit.
                  <br />
                  <br />
                  If you pick{' '}
                  <Text
                    variant="inherit"
                    component="strong"
                    className={classes.text_strong_purple}
                  >
                    Standard
                  </Text>
                  , we&apos;ll pay for your damage up to your coverage limit
                  without reduction for the amount able to be recovered from the
                  at-fault driver&apos;s Liability coverage.
                  <br />
                  <br />
                  You&apos;ll pick either{' '}
                  <Text
                    variant="inherit"
                    component="strong"
                    className={classes.text_strong_purple}
                  >
                    Alternative
                  </Text>{' '}
                  or{' '}
                  <Text
                    variant="inherit"
                    component="strong"
                    className={classes.text_strong_purple}
                  >
                    Standard
                  </Text>{' '}
                  with your Uninsured/Underinsured Motorist Bodily Injury
                  coverage limit selection.
                </>
              ),
            };

            draft.incomeLossLimit = {
              title: 'Income Loss Benefits',
              description: `An accident becomes more than a rough day if you or someone you love can't return to work as a direct result of injuries. If that happens, this coverage can help until you're back on your feet.`,
              modalContent:
                'Coverage can pay for lost wages while you are unable to return to work due to injuries directly sustained in an automobile accident, up to the coverage limit available.',
              caption: `per person per week (up to maximum)`,
            };
          }),
        visibleCoverages: (visibleCoverages: TConfig['visibleCoverages']) => {
          return {
            ...visibleCoverages,
            protectionCoverages: [
              ...visibleCoverages.protectionCoverages.filter(
                ({ key }) =>
                  ![
                    'pet_passenger',
                    'underinsured_motorist_bodily_injury_limit',
                    'uninsured_motorist_property_damage_deductible',
                    'uninsured_motorist_bodily_injury_limit',
                    'income_loss_limit',
                  ].includes(key)
              ),
              {
                label: 'Income loss benefits',
                key: 'income_loss_limit',
              },
              {
                label: 'Uninsured/Underinsured Motorist Property Damage',
                key: 'uninsured_motorist_property_damage_deductible',
                overrideValueLabel: 'Deductible',
              },
              {
                label: 'Uninsured/Underinsured Motorist Bodily Injury',
                key: 'uninsured_motorist_bodily_injury_limit',
              },
              visibleCoverages.protectionCoverages.find(
                ({ key }) => key === 'pet_passenger'
              ),
            ],
            vehicleCoverages: [
              ...visibleCoverages.vehicleCoverages.filter(
                ({ key }) =>
                  !['uninsured_motorist_property_damage_limit'].includes(key)
              ),
            ],
          };
        },
        initialCoverageValues: (
          initialCoverageValues: TConfig['initialCoverageValues']
        ) =>
          produce(initialCoverageValues, (draft) => {
            draft.gap = 'false';
            draft.temporary_ride = 'null';
            draft.side_hustle = 'false';
            draft.uninsured_motorist_property_damage_limit = '0';
          }),
        vehicleSideEffectMessages: (
          vehicleSideEffectMessages: TConfig['vehicleSideEffectMessages']
        ) =>
          produce(vehicleSideEffectMessages, (draft) => {
            draft.temporary_ride =
              'This coverage is only available if Collision and Other than Collision are also selected.';
            draft.gap =
              'This coverage is only available if Collision and Other than Collision are also selected, the vehicle is financed or leased, and the model year is four (4) years old or less.';
            draft.new_car_replacement = `This coverage is only available with a model year that's 2 years old or less.`;
            draft.oem_parts = `This coverage is only available with a model year that's 10 years old or less.`;
            draft.towing_road_services = `This coverage is only available if Comprehensive and Collision are also selected.`;
            draft.temporary_ride = `This coverage is only available if Comprehensive and Collision are also selected.`;
          }),
      }
    : {};
}
export default ToggleVAConfig;
